import { useState } from "react";
import { FileText, Download, Eye, Thermometer, ShieldCheck, ClipboardList } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const documents = [
  {
    id: "temperature",
    icon: Thermometer,
    title: "Temperature Monitoring Log",
    desc: "Pre-formatted daily log for fridges, freezers, hot holding and cooking probes with out-of-range action columns.",
    tags: ["Daily Use", "HACCP Required"],
  },
  {
    id: "haccp",
    icon: ShieldCheck,
    title: "Food Safety Management Document",
    desc: "Complete critical control points template with hazard analysis, monitoring methods, and corrective actions.",
    tags: ["EHO Ready", "Core Document"],
  },
  {
    id: "allergen",
    icon: ClipboardList,
    title: "Allergen Matrix & Training Record",
    desc: "Full 14-allergen matrix for your menu plus staff training log with completion tracking.",
    tags: ["Legal Requirement", "Staff Records"],
  },
];

function DocumentCard({ doc, isActive, onPreview }) {
  return (
    <div
      data-testid={`doc-card-${doc.id}`}
      onClick={() => onPreview(doc.id)}
      className={`gold-card p-6 cursor-pointer transition-all duration-300 ${
        isActive ? "border-[#c9a84c]/50 bg-[#c9a84c]/[0.06]" : ""
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
          isActive ? "bg-[#c9a84c]/20 border border-[#c9a84c]/40" : "bg-white/5 border border-white/10"
        }`}>
          <doc.icon size={18} className={isActive ? "text-gold" : "text-zinc-500"} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading text-base font-medium text-white">{doc.title}</h4>
          <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{doc.desc}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {doc.tags.map((tag) => (
              <span
                key={tag}
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#c9a84c]/10 text-[#c9a84c]/70 border border-[#c9a84c]/15"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DocumentPreview({ docId }) {
  const isTemp = docId === "temperature";
  const isHaccp = docId === "haccp";

  return (
    <div
      data-testid="doc-preview-panel"
      className="rounded-lg border border-[#c9a84c]/20 overflow-hidden bg-[#0a0a0a] shadow-2xl shadow-[#c9a84c]/5"
    >
      {/* PDF-style header */}
      <div className="bg-[#0a0a0a] border-b border-[#c9a84c]/30 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-heading text-base font-semibold text-white">Chef </span>
            <span className="font-heading text-base font-semibold text-gold">Compliance</span>
            <span className="font-heading text-base font-semibold text-white"> UK</span>
          </div>
          <span className="text-[10px] text-[#c9a84c]/60 uppercase tracking-widest">Sample Document</span>
        </div>
        <p className="text-[10px] text-zinc-600 mt-1">
          Food Safety & HACCP Compliance Specialists &middot; Danny Davies &middot; chefcomplianceuk.com
        </p>
      </div>
      <div className="h-[1px] bg-[#c9a84c]/40" />

      {/* Document title */}
      <div className="px-6 py-4 bg-[#141414] border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-[3px] h-8 bg-[#c9a84c] rounded-full" />
          <div>
            <h3 className="font-heading text-sm font-semibold text-white">
              {isTemp ? "Daily Temperature Monitoring Log" : isHaccp ? "Food Safety Management Document" : "Allergen Matrix & Staff Training Record"}
            </h3>
            <p className="text-[10px] text-[#c9a84c]/50 mt-0.5">
              Ref: CCU-2025-SAMPLE &middot; Date: {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
        </div>
      </div>

      {/* Business details */}
      <div className="px-6 py-3 bg-[#111]">
        <p className="text-[10px] text-[#c9a84c]/60 uppercase tracking-widest mb-2 font-semibold">Business Details</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {["Business Name", "Address", "EHO Rating", "Last Inspection"].map((f) => (
            <div key={f} className="flex items-center gap-2">
              <span className="text-[10px] text-zinc-500">{f}:</span>
              <span className="text-[10px] text-zinc-700 flex-1 border-b border-dotted border-zinc-800">&nbsp;</span>
            </div>
          ))}
        </div>
      </div>

      {/* Table preview */}
      <div className="px-6 py-4">
        <p className="text-[10px] text-[#c9a84c]/60 uppercase tracking-widest mb-3 font-semibold">
          {isTemp ? "Temperature Log" : isHaccp ? "Critical Control Points (CCPs)" : "Allergen Matrix"}
        </p>
        <div className="overflow-x-auto rounded border border-zinc-800">
          <table className="w-full text-[9px]">
            <thead>
              <tr className="bg-[#141414]">
                {(isTemp
                  ? ["Date", "Time", "Item / Location", "Temp (°C)", "Action", "Signed"]
                  : isHaccp
                  ? ["CCP", "Process", "Hazard", "Limit", "Monitor", "Action"]
                  : ["Dish", "Glu", "Egg", "Fsh", "Pnt", "Milk", "Nut", "Ses", "Soy"]
                ).map((h) => (
                  <th key={h} className="text-gold px-2 py-2 text-center font-semibold border-b border-zinc-800">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isHaccp ? (
                <>
                  <tr className="bg-[#0d0d0d]">
                    <td className="px-2 py-1.5 text-center text-zinc-400 border-b border-zinc-900">CCP-1</td>
                    <td className="px-2 py-1.5 text-center text-zinc-400 border-b border-zinc-900">Chilled Storage</td>
                    <td className="px-2 py-1.5 text-center text-zinc-400 border-b border-zinc-900">Bacterial growth</td>
                    <td className="px-2 py-1.5 text-center text-zinc-400 border-b border-zinc-900">0-5°C</td>
                    <td className="px-2 py-1.5 text-center text-zinc-400 border-b border-zinc-900">Twice daily</td>
                    <td className="px-2 py-1.5 text-center text-zinc-400 border-b border-zinc-900">Discard/re-chill</td>
                  </tr>
                  <tr className="bg-[#111]">
                    <td className="px-2 py-1.5 text-center text-zinc-400 border-b border-zinc-900">CCP-2</td>
                    <td className="px-2 py-1.5 text-center text-zinc-400 border-b border-zinc-900">Cooking</td>
                    <td className="px-2 py-1.5 text-center text-zinc-400 border-b border-zinc-900">Pathogen survival</td>
                    <td className="px-2 py-1.5 text-center text-zinc-400 border-b border-zinc-900">75°C core</td>
                    <td className="px-2 py-1.5 text-center text-zinc-400 border-b border-zinc-900">Probe check</td>
                    <td className="px-2 py-1.5 text-center text-zinc-400 border-b border-zinc-900">Continue cooking</td>
                  </tr>
                </>
              ) : (
                [0, 1, 2].map((r) => (
                  <tr key={r} className={r % 2 === 0 ? "bg-[#0d0d0d]" : "bg-[#111]"}>
                    {(isTemp ? Array(6) : Array(9)).fill(null).map((_, c) => (
                      <td key={c} className="px-2 py-1.5 text-center text-zinc-700 border-b border-zinc-900">&mdash;</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Fade-out hint */}
        <div className="h-8 bg-gradient-to-b from-transparent to-[#0a0a0a] -mt-4 relative z-10" />
      </div>

      {/* Declaration hint */}
      <div className="px-6 pb-4">
        <div className="rounded border border-zinc-800 bg-[#111] px-4 py-3">
          <p className="text-[10px] text-[#c9a84c]/50 uppercase tracking-widest font-semibold mb-1">Manager Declaration</p>
          <p className="text-[9px] text-zinc-600 leading-relaxed">
            I confirm that all information recorded in this document is accurate and that food safety procedures are being followed in accordance with UK food safety legislation...
          </p>
        </div>
      </div>

      {/* Footer bar */}
      <div className="h-[1px] bg-[#c9a84c]/30" />
      <div className="px-6 py-2.5 bg-[#0a0a0a] flex items-center justify-between">
        <span className="text-[9px] text-[#c9a84c]/40">Chef Compliance UK &middot; Danny Davies</span>
        <span className="text-[9px] text-zinc-700">SAMPLE DOCUMENT</span>
      </div>
    </div>
  );
}

export default function Documents() {
  const [activeDoc, setActiveDoc] = useState("temperature");

  const handleDownload = (docId) => {
    window.open(`${API}/documents/sample/${docId}`, "_blank");
  };

  return (
    <section
      data-testid="documents-section"
      className="relative py-24 md:py-32 lg:py-40 bg-[#0a0a0a]/[0.93]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="gold-line mx-auto mb-6" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">
            Sample Documents
          </span>
          <h2
            data-testid="documents-heading"
            className="font-heading text-3xl md:text-4xl font-medium text-white mt-4"
          >
            Professional Templates
            <br />
            <span className="text-zinc-500">Preview & Download</span>
          </h2>
          <p className="mt-4 text-base text-zinc-500 max-w-xl mx-auto">
            See the quality of our documentation before you commit. Download free sample
            templates branded in our signature black & gold.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Document cards */}
          <div className="lg:col-span-4 space-y-4 order-1">
            {documents.map((doc) => (
              <DocumentCard
                key={doc.id}
                doc={doc}
                isActive={activeDoc === doc.id}
                onPreview={setActiveDoc}
              />
            ))}

            {/* Download buttons */}
            <div className="flex flex-col gap-3 pt-4">
              <Button
                data-testid="download-current-btn"
                onClick={() => handleDownload(activeDoc)}
                className="w-full bg-gold hover:bg-[#b8963f] text-black py-3 text-sm font-semibold rounded transition-colors tracking-wide"
              >
                <Download size={16} className="mr-2" />
                Download Sample PDF
              </Button>
              <div className="flex gap-2 sm:gap-3">
                {documents.map((doc) => (
                  <Button
                    key={doc.id}
                    data-testid={`download-btn-${doc.id}`}
                    variant="outline"
                    onClick={() => handleDownload(doc.id)}
                    className="flex-1 border-[#c9a84c]/20 text-gold hover:bg-[#c9a84c]/10 text-xs bg-transparent px-2 sm:px-4"
                    title={`Download ${doc.title}`}
                  >
                    <doc.icon size={14} className="mr-1.5" />
                    {doc.id === "temperature" ? "Temp" : doc.id === "haccp" ? "HACCP" : "Allergen"}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Document preview */}
          <div className="lg:col-span-8 order-2">
            <div className="flex items-center gap-2 mb-4 hidden lg:flex">
              <Eye size={14} className="text-gold" />
              <span className="text-xs text-zinc-500 uppercase tracking-widest">Live Preview</span>
            </div>
            <DocumentPreview docId={activeDoc} />
          </div>
        </div>
      </div>
    </section>
  );
}
